import React from "react";
import { createRoot } from "react-dom/client";
import ChatWidget, { ChatWidgetProps } from "./components/ChatWidget";
// Import CSS as an inline string for Shadow DOM injection.
// This prevents widget styles from leaking into the host page (e.g. Shopify stores).
import widgetStyles from "./index.css?inline";
import { identifyVisitor, saveContactId } from "./utils/visitor-identification";
import { VisitorInfo } from "./shared/types/chat-widget-visitor";
import { WidgetContextProvider } from "./contexts/WidgetContext";
import { ASSET_BASE } from "./lib/asset-base";

// Widget initialization function
type ChatWidgetOptions = Omit<
  ChatWidgetProps,
  "organizationId" | "visitorInfo" | "widgetId"
> & {
  containerId?: string;
  organizationId?: string;
  widgetId?: string;
  visitorInfo?: VisitorInfo;
  previewMode?: string;
  isRylo?: boolean;
};

// Default options
const defaultOptions: ChatWidgetOptions = {
  containerId: "react-chat-widget-container",
  position: "bottom-right",
  theme: "dark",
};

// The React root of a programmatically-mounted widget, kept so the host can
// tear it down again via ReactChatWidget.destroy() (host-controlled mode).
let activeRoot: ReturnType<typeof createRoot> | null = null;

/**
 * Process CSS for Shadow DOM injection:
 * 1. Replace :root with :host to scope CSS variables to the shadow host
 * 2. Convert all rem units to fixed px values for consistent rendering
 *    regardless of the host page's root font-size.
 *    Many Shopify themes use font-size: 62.5% (10px) on :root which
 *    makes 1rem = 10px instead of the standard 16px, breaking all
 *    Tailwind rem-based spacing, typography, and sizing utilities.
 */
const processWidgetStyles = (css: string): string => {
  let processed = css;
  // Scope CSS variables to the shadow host instead of document root
  processed = processed.replace(/:root/g, ':host');
  // Convert rem to fixed px (standard 1rem = 16px)
  processed = processed.replace(/(\d*\.?\d+)rem\b/g, (_match, num) => {
    return `${Math.round(parseFloat(num) * 16 * 100) / 100}px`;
  });
  // Add base styles that normally target html/body (which don't exist in Shadow DOM)
  processed += `
    :host {
      display: block;
      font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
      font-size: 16px;
      line-height: 1.5;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      -webkit-text-size-adjust: 100%;
    }
  `;
  return processed;
};

// Create container with Shadow DOM for complete style isolation.
// Shadow DOM prevents both:
// - Widget CSS from leaking into the host page (Tailwind base resets)
// - Host page CSS from affecting the widget (Shopify theme overrides)
const createContainer = (containerId: string): HTMLElement => {
  let host = document.getElementById(containerId);

  if (!host) {
    host = document.createElement("div");
    host.id = containerId;
    document.body.appendChild(host);
  }

  // Force visibility on host element to prevent Shopify themes
  // (and other aggressive CSS resets) from hiding the container
  host.style.setProperty('display', 'block', 'important');
  host.style.setProperty('visibility', 'visible', 'important');
  host.style.setProperty('opacity', '1', 'important');
  host.style.setProperty('position', 'static', 'important');
  host.style.setProperty('width', '0', 'important');
  host.style.setProperty('height', '0', 'important');
  host.style.setProperty('overflow', 'visible', 'important');

  // Create Shadow DOM if not already present
  let shadow = host.shadowRoot;
  if (!shadow) {
    shadow = host.attachShadow({ mode: 'open' });

    // Inject processed styles into Shadow DOM
    const styleEl = document.createElement('style');
    styleEl.textContent = processWidgetStyles(widgetStyles);
    shadow.appendChild(styleEl);
  }

  // Get or create render target inside Shadow DOM
  let renderTarget = shadow.querySelector('#chat-widget-root') as HTMLElement;
  if (!renderTarget) {
    renderTarget = document.createElement('div');
    renderTarget.id = 'chat-widget-root';
    shadow.appendChild(renderTarget);
  }

  // Get or create portal container for Radix UI portals (Select, Dialog, etc.)
  let portalTarget = shadow.querySelector('#chat-widget-portals') as HTMLElement;
  if (!portalTarget) {
    portalTarget = document.createElement('div');
    portalTarget.id = 'chat-widget-portals';
    shadow.appendChild(portalTarget);
  }

  return renderTarget;
};

// Fetch widget configuration from API
const fetchWidgetConfig = async (organizationId: string, widgetId?: string) => {
  try {
    // Get the origin URL from the window location
    const baseUrl = ASSET_BASE;
    // API endpoint URL
    // widgetId is OPTIONAL (18 Sep 2026): left off, the endpoint resolves the
    // org's only widget (or the one flagged is_active) and returns its id.
    const url = `${baseUrl}/api/config-chat-widget?organizationId=${encodeURIComponent(
      organizationId
    )}${widgetId ? `&widgetId=${encodeURIComponent(widgetId)}` : ""}`;

    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Error fetching widget config:", errorData);
      throw new Error("Failed to fetch widget configuration");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching widget config:", error);
    return null;
  }
};

/**
 * Is a STORE CUSTOMER signed in, and who? Same source the popup widget and the
 * loader use: `ShopifyAnalytics.meta.page.customerId` is populated by Shopify
 * ONLY for a signed-in customer (`__st.cid` / `meta.page.customerId` are the
 * older globals). Read at init, not at parse time — the theme injects it.
 * Anything absent (or a non-Shopify site) means "not signed in".
 */
const readShopifyCustomerId = (): string | undefined => {
  try {
    const g = window as unknown as Record<string, any>;
    const id =
      g?.ShopifyAnalytics?.meta?.page?.customerId ??
      g?.__st?.cid ??
      g?.meta?.page?.customerId;
    return id ? String(id) : undefined;
  } catch {
    return undefined;
  }
};

// Initialize chat widget.
// Accepts optional explicit ids so it can be called programmatically
// (e.g. from a SPA / dynamic injection where `document.currentScript` is null).
// Falls back to the script tag's data-* attributes for the classic embed.
const initChatWidget = async (opts?: {
  organizationId?: string;
  widgetId?: string;
  // Host-controlled mode (e.g. the FlowIQ app sidebar question-mark launcher).
  ryloMode?: boolean; // open in the centred ("Rylo") modal style
  hideLauncher?: boolean; // host supplies its own launcher — hide the built-in one
  defaultOpen?: boolean; // mount with the chat already open
  onClose?: () => void; // called when the user closes (host tears down)
  // Pre-identified contact (e.g. a logged-in FlowIQ member resolved by the host).
  // Seeds the widget's identity so it opens without the info form.
  contactId?: string;
  userInfo?: { fullName?: string; email?: string; phone?: string };
  // Signed-in STORE customer (Shopify customer id). The loader passes it; the
  // classic embed (data-auto-init) has no loader, so when absent we read it
  // ourselves — otherwise audience-tagged quick actions never see a sign-in.
  shopifyCustomerId?: string | null;
  // Signed-in customer's first name. The Shopify app embed renders it
  // server-side (`{{ customer.first_name }}`) into data-customer-first-name,
  // so it is only ever present for the real signed-in person.
  shopifyCustomerFirstName?: string | null;
}): Promise<void> => {
  const container = createContainer(defaultOptions.containerId!);

  // Get configuration from explicit options first, else the script tag
  const scriptTag = document.currentScript as HTMLScriptElement;
  const organizationId = opts?.organizationId || scriptTag?.dataset.organizationId;
  const widgetId = opts?.widgetId || scriptTag?.dataset.widgetId;
  // Sign-in: the app embed's server-rendered data-customer-id is the most
  // reliable source (Liquid only writes it for a signed-in customer); the
  // loader may pass one; else read Shopify's analytics globals.
  const shopifyCustomerId =
    opts?.shopifyCustomerId ?? scriptTag?.dataset.customerId ?? readShopifyCustomerId();
  const shopifyCustomerFirstName =
    (opts?.shopifyCustomerFirstName ?? scriptTag?.dataset.customerFirstName ?? "").trim() || undefined;

  if (!organizationId) {
    console.error(
      "Organization ID is required. Add data-organization-id attribute to the script tag."
    );
    return;
  }

  // Host-provided pre-identified contact (e.g. a logged-in FlowIQ member). Seed
  // the same localStorage the widget already reads (contact id + user info) so
  // identifyVisitor picks it up and the chat opens without the info form.
  if (opts?.contactId) {
    saveContactId(organizationId, opts.contactId);
  }
  if (opts?.userInfo) {
    localStorage.setItem(
      `flowapt_user_${organizationId}`,
      JSON.stringify({
        fullName: opts.userInfo.fullName || "",
        email: opts.userInfo.email || "",
        phone: opts.userInfo.phone || "",
      })
    );
  }

  // Get visitor information
  const visitorInfo = identifyVisitor(organizationId);

  // Fetch widget config from the API
  const widgetConfig = await fetchWidgetConfig(organizationId, widgetId);

  // The id everything downstream runs on. A snippet carrying data-widget-id
  // keeps using it; an org-only snippet takes whichever widget the endpoint
  // resolved. Without one there is nothing to stamp on a message, so stop.
  const effectiveWidgetId: string | undefined = widgetId || widgetConfig?.widgetId;
  if (!effectiveWidgetId) {
    console.error(
      "No widget could be resolved for this organization. Add data-widget-id to the script tag, or set an active widget in FlowIQ."
    );
    return;
  }

  // If we couldn't fetch the config, use fallback values from data attributes
  const headerText = widgetConfig?.headerText || scriptTag?.dataset.headerText;
  const subheaderText =
    widgetConfig?.subheaderText || scriptTag?.dataset.subheaderText;
  const preloginHeaderText =
    widgetConfig?.preloginHeaderText || scriptTag?.dataset.preloginHeaderText;
  const preloginSubheaderText =
    widgetConfig?.preloginSubheaderText ||
    scriptTag?.dataset.preloginSubheaderText;
  const position =
    widgetConfig?.position ||
    (scriptTag?.dataset.position as ChatWidgetProps["position"]) ||
    defaultOptions.position;
  const theme =
    (scriptTag?.dataset.theme as ChatWidgetProps["theme"]) ||
    defaultOptions.theme;
  const previewMode = scriptTag?.dataset.previewMode;
  const ryloMode = opts?.ryloMode ?? (scriptTag?.getAttribute("rylo") === "true");
  // Prefer the DB config (web_chat_widgets.two_fa.enabled) over the data-2fa script attribute
  const twoFa =
    widgetConfig?.twoFa === true ||
    scriptTag?.getAttribute("data-2fa") === "true";

  // Custom icons - prefer config from server but fallback to data attributes
  let customIcons;

  if (widgetConfig?.customIcons) {
    // Create React elements from icon URLs in widget config
    customIcons = {
      userWeb: widgetConfig.customIcons.userWeb ? (
        <img
          src={widgetConfig.customIcons.userWeb.url}
          alt="Home Icon"
          className="w-full h-full object-contain"
        />
      ) : undefined,
      botWeb: widgetConfig.customIcons.botWeb ? (
        <img
          src={widgetConfig.customIcons.botWeb.url}
          alt="Bot"
          width={16}
          height={16}
        />
      ) : undefined,
      humanWeb: widgetConfig.customIcons.humanWeb ? (
        <img
          src={widgetConfig.customIcons.humanWeb.url}
          alt="Support"
          width={16}
          height={16}
        />
      ) : undefined,
    };
  } else {
    // Fallback to data attributes if no config from server
    const userIconUrl = scriptTag?.dataset.iconUser;
    const botIconUrl = scriptTag?.dataset.iconBot;
    const humanIconUrl = scriptTag?.dataset.iconHuman;

    customIcons =
      userIconUrl || botIconUrl || humanIconUrl
        ? {
            userWeb: userIconUrl ? (
              <img src={userIconUrl} alt="Home Icon" className="w-full h-full object-contain" />
            ) : undefined,
            botWeb: botIconUrl ? (
              <img src={botIconUrl} alt="Bot" width={16} height={16} />
            ) : undefined,
            humanWeb: humanIconUrl ? (
              <img src={humanIconUrl} alt="Support" width={16} height={16} />
            ) : undefined,
          }
        : undefined;
  }

  // Use colors from widget config or fallback to data attributes
  const customColors = widgetConfig?.customColors || {
    backgroundColor: scriptTag?.getAttribute("data-background-color"),
    textColor: scriptTag?.getAttribute("data-text-color"),
    secondaryTextColor: scriptTag?.getAttribute("data-secondary-text-color"),
    borderColor: scriptTag?.getAttribute("data-border-color"),
    inputBackgroundColor: scriptTag?.getAttribute(
      "data-input-background-color"
    ),
    buttonBackgroundColor: scriptTag?.getAttribute(
      "data-button-background-color"
    ),
    buttonTextColor: scriptTag?.getAttribute("data-button-text-color"),
  };

  // Filter out undefined values
  const definedColors = Object.fromEntries(
    Object.entries(customColors).filter(
      ([_, value]) => value !== null && value !== undefined
    )
  );

  // Create React root and render widget
  // Get the portal container for Radix UI components (Select, Dialog, etc.)
  const shadow = container.getRootNode() as ShadowRoot;
  const portalContainer = shadow.querySelector('#chat-widget-portals') as HTMLElement;

  const root = createRoot(container);
  activeRoot = root;
  root.render(
    <WidgetContextProvider value={portalContainer}>
      <ChatWidget
        position={position}
        theme={theme}
        organizationId={organizationId}
        widgetId={effectiveWidgetId}
        visitorInfo={visitorInfo}
        headerText={headerText || undefined}
        subheaderText={subheaderText || undefined}
        preloginHeaderText={preloginHeaderText || undefined}
        preloginSubheaderText={preloginSubheaderText || undefined}
        agentName={widgetConfig?.agentName || undefined}
        customIcons={customIcons}
        customColors={
          Object.keys(definedColors).length > 0 ? definedColors : undefined
        }
        previewMode={previewMode}
        ryloMode={ryloMode}
        hideLauncher={opts?.hideLauncher}
        defaultOpen={opts?.defaultOpen}
        onClose={opts?.onClose}
        shopifyCustomerId={shopifyCustomerId}
        shopifyCustomerFirstName={shopifyCustomerFirstName}
        twoFa={twoFa}
        whatsapp={widgetConfig?.whatsapp}
        messenger={widgetConfig?.messenger}
        shopifyWebsite={widgetConfig?.shopifyWebsite}
        storefront={widgetConfig?.storefront}
        iconDisplay={widgetConfig?.iconDisplay}
        centerConfig={widgetConfig?.centerConfig}
        quickActions={widgetConfig?.quickActions}
        teamAvatars={widgetConfig?.teamAvatars}
        homeCopy={widgetConfig?.homeCopy}
        userInfoConfig={widgetConfig?.userInfoConfig}
        homeCards={widgetConfig?.homeCards}
        homeDesign={widgetConfig?.homeDesign}
        orgSlug={widgetConfig?.orgSlug}
        visitorCountry={widgetConfig?.visitorCountry}
        loopEnabled={widgetConfig?.loopEnabled}
        flowiqSubsEnabled={widgetConfig?.flowiqSubsEnabled}
        manageSubsEnabled={widgetConfig?.manageSubsEnabled}
        manageNoLogin={widgetConfig?.manageNoLogin}
        subscriptionWeeks={widgetConfig?.subscriptionWeeks}
        loyaltyEnabled={widgetConfig?.loyaltyEnabled}
        rewardsConfig={widgetConfig?.rewardsConfig}
        launcher={widgetConfig?.launcher}
        hiddenPaths={widgetConfig?.hiddenPaths}
      />
    </WidgetContextProvider>
  );

  // Make options available globally
  window.__CHAT_WIDGET_OPTIONS__ = {
    ...defaultOptions,
    position,
    theme,
    organizationId,
    widgetId: effectiveWidgetId,
    headerText: headerText || undefined,
    subheaderText: subheaderText || undefined,
    customIcons,
    customColors:
      Object.keys(definedColors).length > 0 ? definedColors : undefined,
    previewMode,
    isRylo: ryloMode,
    twoFa,
  };
};

// Tear down a programmatically-mounted widget (host-controlled mode).
// Unmounts the React root and removes the host container from the DOM, so a
// later init() mounts a clean instance.
const destroyChatWidget = (): void => {
  try {
    activeRoot?.unmount();
  } catch {
    /* noop */
  }
  activeRoot = null;
  const host = document.getElementById(defaultOptions.containerId!);
  host?.remove();
};

// Expose init function to window
declare global {
  interface Window {
    ReactChatWidget: {
      init: typeof initChatWidget;
      initChatWidget: typeof initChatWidget;
      destroy: typeof destroyChatWidget;
    };
    __CHAT_WIDGET_OPTIONS__?: ChatWidgetOptions;
  }
}

// Initialize the global object
if (typeof window !== "undefined") {
  window.ReactChatWidget = {
    init: initChatWidget,
    initChatWidget: initChatWidget,
    destroy: destroyChatWidget,
  };
}

// Auto-initialize if script has data-auto-init attribute
if (typeof document !== "undefined") {
  const scriptTag = document.currentScript as HTMLScriptElement;
  if (scriptTag && scriptTag.dataset.autoInit) {
    initChatWidget();
  }
}

export default initChatWidget;
